/**
 * Shared sky-state types (Phase W).
 *
 * GDD §7.5 + §17 #16: a server-authoritative "dimming star" sits above the
 * overworld sky. Its brightness dims 1 % per in-world full moon; at 100 %
 * dimming the years-long "arrival of the One" endgame arc (§4.1) begins.
 *
 * Phase W wires only the baseline — a single broadcast-only message that
 * carries the authoritative counter. The client never sends a sky message:
 * this is read-only from the player's perspective, matching the GDD's
 * "always there, no menu" framing.
 *
 * Canonical reference: docs/pivot-overnight-plan-4.md §2 Phase W.
 *
 * @packageDocumentation
 */
// ── Message constants ───────────────────────────────────────────────────────
/**
 * Server → all clients: current dimming-star state. Broadcast once on join
 * (per-client `send`) and again on every in-world full-moon tick (broadcast
 * to room). Broadcast-only — there is no client→server direction.
 */
export const MSG_SKY_STATE = 'sky_state';
/**
 * Server → all clients: a milestone threshold has been crossed by the
 * dimming counter. Broadcast at the moment of crossing. Each milestone
 * fires at most once per server, ever. Broadcast-only — there is no
 * client→server direction.
 *
 * Spec: B-029, `docs/design/star-milestones-and-sunset.md` §1.
 */
export const MSG_STAR_MILESTONE = 'star_milestone';
// ── Season tick (Round 11 Phase A) ──────────────────────────────────────
//
// Server → all clients: the in-world day-of-season changed. Broadcast once
// per in-world day transition (aligned with the full-moon polling interval).
// Clients use this for seasonal UI cues (sky palette, festival banners).
/**
 * Server → all clients: current season state. Broadcast when the in-world
 * day-of-season changes. Broadcast-only — no client→server direction.
 */
export const MSG_SEASON_TICK = 'season_tick';
// ── Milestone Mini-Event Messages ───────────────────────────────────────
// Phase OO — three mini-events fired by the server during milestone
// multi-day event windows. Each is broadcast-only (server → all clients).
// Spec: `docs/design/star-milestones-and-sunset.md` §1.2, §1.3, §1.5.
/**
 * Server → all clients: begin the Ember Drift mini-event (§1.2 "The
 * First Noticing" moment). Warm-colored motes rise from every hearth
 * toward the star for `durationSec` real seconds.
 */
export const MSG_MILESTONE_EMBER_DRIFT = 'milestone_ember_drift';
/**
 * Server → all clients: begin The Pause mini-event (§1.5 "The Hush"
 * moment). A quiet overlay text card appears for `durationSec` real
 * seconds with a gentle fade-in.
 */
export const MSG_MILESTONE_THE_PAUSE = 'milestone_the_pause';
/**
 * Server → dreaming clients: the Shared Dream sequence (§1.3 "The
 * Halfway Vigil" moment). Delivered only to players in Dreaming
 * attendance state. The client renders the three dream fragments
 * additively inside SleepOverlayScene.
 */
export const MSG_MILESTONE_SHARED_DREAM = 'milestone_shared_dream';
/**
 * Server → all clients: begin the Root-Song mini-event (§1.4 "The
 * Trembling" moment). Every tree in every realm emits faint particles
 * drifting upward for `durationSec` real seconds — the roots remember.
 * One-shot per server lifetime.
 */
export const MSG_MILESTONE_ROOT_SONG = 'milestone_root_song';
/**
 * Server → all clients: begin the First Dimming mini-event (§1.2 "The First
 * Noticing" moment). The sky tint shifts subtly — the first visible sign the
 * star is changing. Lasts `durationSec` real seconds.
 */
export const MSG_MILESTONE_FIRST_DIMMING = 'milestone_first_dimming';
/**
 * Server → all clients: begin The Gathering mini-event (§1.3 "The Halfway
 * Vigil" moment). All online players briefly see each other's ghost-outlines
 * regardless of which realm they occupy. Lasts `durationSec` real seconds.
 */
export const MSG_MILESTONE_THE_GATHERING = 'milestone_the_gathering';
/**
 * Server → all clients: begin the Quiet Reckoning mini-event (§1.6 "The
 * Setting of the Table" moment). Each player receives a personal milestone
 * Whisper unique to their journey.
 */
export const MSG_MILESTONE_QUIET_RECKONING = 'milestone_quiet_reckoning';
/**
 * Server → all clients: begin The Sunset mini-event (§1.7 "The Stillness"
 * moment). The final event before the approach completes — a warm, sweeping
 * atmospheric overlay that marks the star's last light. Lasts `durationSec`
 * real seconds.
 */
export const MSG_MILESTONE_THE_SUNSET = 'milestone_the_sunset';
// ── Festival state change (Round 11 Phase B) ───────────────────────────
//
// Server → all clients: a festival's lifecycle phase has changed. Broadcast
// on every phase advance (idle→telegraph→preparation→festival_day→completed/quiet).
// Clients use this for festival UI banners and preparation-counter displays.
/**
 * Server → all clients: festival phase changed. Broadcast-only — no
 * client→server direction.
 */
export const MSG_FESTIVAL_STATE_CHANGED = 'festival_state_changed';
//# sourceMappingURL=sky.js.map